import { Injectable } from '@angular/core';
import {
  CanActivate,
  Router,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { filter, map, take, catchError, timeout } from 'rxjs/operators';
import { ProfileService } from '../services/profile.service';
import { Profile } from '../models/profile.model';

@Injectable({
  providedIn: 'root',
})
export class RoleRedirectGuard implements CanActivate {
  constructor(
    private router: Router,
    private profileService: ProfileService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | boolean | UrlTree {
    const targetUrl = state.url;
    const mode = localStorage.getItem('mode');

    // 1. If in-memory profile is already loaded from server API, check immediately
    if (this.profileService.profileDataSubject.value) {
      return this.checkRoleAccess(
        this.profileService.profileDataSubject.value.rajaongkir_level,
        mode,
        targetUrl
      );
    }

    // 2. Otherwise fetch profile from server and wait for authoritative stream
    this.profileService.getProfile();

    return this.profileService.profileData$.pipe(
      filter((profile): profile is Profile => !!profile),
      take(1),
      timeout(4000),
      map((profile) => {
        return this.checkRoleAccess(profile.rajaongkir_level, mode, targetUrl);
      }),
      catchError(() => {
        const verifiedLevel = this.profileService.getVerifiedStoredLevel();
        if (verifiedLevel > 0) {
          return of(this.checkRoleAccess(verifiedLevel, mode, targetUrl));
        }
        this.router.navigate(['/dashboard']);
        return of(false);
      })
    );
  }

  private checkRoleAccess(
    roLevel: number | string | null | undefined,
    mode: string | null,
    targetUrl: string
  ): boolean {
    const restrictedRoutes = [
      '/order-data',
      '/ticketing',
      '/rates',
      '/rates-track',
      '/misc/simulator',
      '/account/teams',
      '/account/expedition',
      '/misc/custom-label',
    ];

    const levelStr = String(roLevel ?? '');
    if (levelStr !== '3') {
      if (restrictedRoutes.some((route) => targetUrl.startsWith(route))) {
        this.router.navigate(['/dashboard']);
        return false;
      }
    }

    if (mode === 'production') {
      if (targetUrl.startsWith('/misc/simulator')) {
        this.router.navigate(['/dashboard']);
        return false;
      }
    }

    return true;
  }
}

